import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useEffect,
} from "react";
import { useSelector } from "react-redux";
import classNames from "classnames";

import { getTemplateSetting } from "store/selectors/templateSettings.js";

import { TableBase } from "components/uiComponents/TableBase";

import { columnConverter } from "../lib/column-converter";
import { useRowSelection } from "../model/use-row-selection";

import TableWrapperInner from "./TableWrapperInner";

import styles from "./table-styles.module.scss";
import CustomFooterCell from "../CustomFooterCell";
import { CustomBodyCell } from "components/uiComponents/CustomBodyCellV2";

const Table = forwardRef(
  (
    {
      columns = [],
      data = [],
      footer = {},
      initialState,
      columnVisibility,
      ActionsComponent,
      HeaderCustomActions,
      onRowSelectionChange,
      onRowsChange,
      withInverseAllSelection,
      wrapperInnerClassName,
      tableStyles = {},
      columnsInfo,
      withOutActions,
      isExternalWindow = false,
      modelId = null,
    },
    ref
  ) => {
    const processedColumns = useMemo(() => columnConverter(columns), [columns]);
    const templateSettings = useSelector(getTemplateSetting);
    const { rowSelection, handleRowSelectionChange, handleClearSelection } =
      useRowSelection(data, onRowSelectionChange, {
        initialRowSelection: isExternalWindow ? initialState?.rowSelection || {} : null,
        disableLocalStorage: !!isExternalWindow,
        modelId: modelId,
      });

    useEffect(() => {
      // In external (detached) table window we must preserve incoming rowSelection,
      // otherwise opening the window clears the carried-over selection immediately.
      if (isExternalWindow) return;
      const { categories, groups } = templateSettings;
      const shouldClear =
        (Array.isArray(categories) && !!categories.length) ||
        (Array.isArray(groups) && !!groups.length);
      if (shouldClear) {
        handleClearSelection();
      }
    }, [templateSettings]);

    useImperativeHandle(ref, () => ({ handleClearSelection }));

    return (
      <TableBase
        state={{ rowSelection, columnVisibility }}
        initialState={initialState}
        data={data}
        footer={footer}
        columns={processedColumns}
        columnsInfo={columnsInfo}
        isExternalWindow={isExternalWindow}
        modelId={modelId}
        classes={{
          ...styles,
          thead: classNames(
            styles.thead,
            tableStyles.thead,
            isExternalWindow && tableStyles.theadExternal
          ),
          th: classNames(styles.th, tableStyles.th),
        }}
        components={{
          wrapperInner: (props) => (
            <TableWrapperInner className={wrapperInnerClassName} {...props}>
              {ActionsComponent}
            </TableWrapperInner>
          ),
          headerCellCustomActions: HeaderCustomActions,
          bodyCell: CustomBodyCell,
          footerCell: CustomFooterCell,
        }}
        onRowSelectionChange={handleRowSelectionChange}
        onRowsChange={onRowsChange}
        withSorting
        withGlobalFilter
        withRowSelection
        options={{
          enabledInverseAllSelection: withInverseAllSelection,
        }}
        withOutActions={withOutActions}
        headerProps={isExternalWindow ? { style: { top: 0 } } : undefined}
      />
    );
  }
);
export default Table;
